/**
 * Capability Resolver Hook
 *
 * Resolves a user's capabilities from their profile and role definition.
 * This replaces the role-name-based usePermission hook with a capability-based model.
 *
 * Usage:
 *   const { capabilities, hasCapability, isAdmin, isDepartmentHead } = useCapabilities();
 */
import { useMemo } from "react";
import { useAuth } from "@/app/providers/AuthProvider";
import {
  type Capability,
  type RoleKey,
  type DepartmentId,
  CAPABILITIES,
  ROLE_DEFINITIONS,
  GLOBAL_OWNER_ROLES,
  getRoleByKey,
  resolveLegacyRole,
} from "@carmel/shared";

interface CapabilityContext {
  /** All resolved capabilities for the current user. */
  capabilities: Capability[];
  /** The canonical role key for the current user. */
  roleKey: RoleKey | null;
  /** The primary department ID for the current user. */
  departmentId: DepartmentId | null;
  /** Whether the user has a specific capability. */
  hasCapability: (capability: Capability) => boolean;
  /** Whether the user has any of the given capabilities. */
  hasAnyCapability: (caps: Capability[]) => boolean;
  /** Whether the user has all of the given capabilities. */
  hasAllCapabilities: (caps: Capability[]) => boolean;
  /** Whether the user is a global owner (Admin/Superadmin). */
  isAdmin: boolean;
  /** Whether the user is a department head. */
  isDepartmentHead: boolean;
  /** Whether the user can view the oversight module. */
  canViewOversight: boolean;
  /** Whether the user can view department dashboard. */
  canViewDepartmentDashboard: boolean;
}

/**
 * Resolve the canonical role key from a user profile.
 * Falls back from legacy `role` to `primaryRoleId`.
 */
export function resolveCapabilityRoleKey(user: { role: string; primaryRoleId: string | null | undefined }): RoleKey | null {
  // Prefer canonical field only when it maps to a known role.
  if (user.primaryRoleId && getRoleByKey(user.primaryRoleId)) {
    return user.primaryRoleId as RoleKey;
  }
  // Fall back to legacy field
  return resolveLegacyRole(user.role);
}

/**
 * Resolve capabilities from a role key.
 */
function resolveCapabilities(roleKey: RoleKey): Capability[] {
  const roleDef = getRoleByKey(roleKey);
  if (!roleDef) return [];
  return [...roleDef.capabilities] as Capability[];
}

export function useCapabilities(): CapabilityContext {
  const { user } = useAuth();

  return useMemo(() => {
    if (!user) {
      return {
        capabilities: [],
        roleKey: null,
        departmentId: null,
        hasCapability: () => false,
        hasAnyCapability: () => false,
        hasAllCapabilities: () => false,
        isAdmin: false,
        isDepartmentHead: false,
        canViewOversight: false,
        canViewDepartmentDashboard: false,
      };
    }

    const roleKey = resolveCapabilityRoleKey(user);
    const departmentId = (user.primaryDepartmentId ?? user.department) as DepartmentId | null;
    const caps = roleKey ? resolveCapabilities(roleKey) : [];

    const hasCapability = (cap: Capability): boolean => caps.includes(cap);
    const hasAnyCapability = (capsToCheck: Capability[]): boolean =>
      capsToCheck.some((c) => caps.includes(c));
    const hasAllCapabilities = (capsToCheck: Capability[]): boolean =>
      capsToCheck.every((c) => caps.includes(c));

    const isAdmin = roleKey ? GLOBAL_OWNER_ROLES.includes(roleKey as typeof GLOBAL_OWNER_ROLES[number]) : false;
    const roleDef = roleKey ? getRoleByKey(roleKey) : null;
    const isDepartmentHead = roleDef?.isDepartmentHead ?? false;

    return {
      capabilities: caps,
      roleKey,
      departmentId,
      hasCapability,
      hasAnyCapability,
      hasAllCapabilities,
      isAdmin,
      isDepartmentHead,
      canViewOversight: hasCapability(CAPABILITIES.DASHBOARD_VIEW_ORGANISATION),
      canViewDepartmentDashboard: hasCapability(CAPABILITIES.DASHBOARD_VIEW_DEPARTMENT),
    };
  }, [user]);
}
